import React, { useEffect, useState } from 'react';
import { formatCurrency } from '../utils/currencyFormatter';
import { PlusIcon, TrashIcon, SearchIcon, MapPinIcon, BookOpenIcon, XIcon, InfoIcon } from 'lucide-react';
import { getDefaultCurrency } from '../../utils/appSettings';
export function PurchaseOrderForm({
  initialData = null,
  onSave,
  isConversion = false,
  locale = 'en-US'
}) {
  const [formData, setFormData] = useState({
    name: '',
    vendor: '',
    vendorId: '',
    items: [{
      description: '',
      quantity: 1,
      unitPrice: 0,
      type: 'Item',
      uom: 'EA',
      taxCode: 'STD',
      taxRate: 10,
      taxAmount: 0,
      catalogItemId: '' // ID of catalog item if selected from catalog
    }],
    notes: '',
    deliveryDate: '',
    paymentTerms: '30',
    shipmentMethod: '',
    currency: getDefaultCurrency().code,
    deliveryAddress: {
      type: 'entity',
      addressId: '',
      customAddress: {
        name: '',
        street: '',
        city: '',
        state: '',
        postalCode: '',
        country: ''
      }
    },
    deliveryContact: {
      name: '',
      phone: '',
      email: ''
    }
  });
  // Add errors state
  const [errors, setErrors] = useState({
    lineItems: []
  });
  // Sample vendors for the dropdown
  const [vendors, setVendors] = useState([{
    id: 'V001',
    name: 'Office Depot',
    country: 'United States',
    hasCatalog: true
  }, {
    id: 'V002',
    name: 'Dell Technologies',
    country: 'United States',
    hasCatalog: true
  }, {
    id: 'V003',
    name: 'PrintWorks Inc.',
    country: 'Canada',
    hasCatalog: false
  }, {
    id: 'V004',
    name: 'Server Solutions Inc.',
    country: 'United Kingdom',
    hasCatalog: true
  }, {
    id: 'V005',
    name: 'Global Supplies Co.',
    country: 'Australia',
    hasCatalog: false
  }]);
  // Sample catalog items for vendors
  const [vendorCatalogs, setVendorCatalogs] = useState({
    V001: [{
      id: 'CAT001-001',
      name: 'Premium Paper - A4',
      description: 'Premium quality A4 paper, 500 sheets',
      type: 'Item',
      uom: 'EA',
      unitPrice: 12.99,
      taxCode: 'STD',
      taxRate: 10
    }, {
      id: 'CAT001-002',
      name: 'Ballpoint Pens - Blue',
      description: 'Pack of 10 blue ballpoint pens',
      type: 'Item',
      uom: 'EA',
      unitPrice: 5.99,
      taxCode: 'STD',
      taxRate: 10
    }, {
      id: 'CAT001-003',
      name: 'Office Chair - Ergonomic',
      description: 'Ergonomic office chair with lumbar support',
      type: 'Item',
      uom: 'EA',
      unitPrice: 249.99,
      taxCode: 'STD',
      taxRate: 10
    }, {
      id: 'CAT001-004',
      name: 'Desk Organizer',
      description: 'Desktop organizer with multiple compartments',
      type: 'Item',
      uom: 'EA',
      unitPrice: 19.99,
      taxCode: 'STD',
      taxRate: 10
    }],
    V002: [{
      id: 'CAT002-001',
      name: 'Laptop - Business Series',
      description: 'Business laptop with 16GB RAM, 512GB SSD',
      type: 'Item',
      uom: 'EA',
      unitPrice: 1299.99,
      taxCode: 'STD',
      taxRate: 10
    }, {
      id: 'CAT002-002',
      name: 'Desktop Computer - Standard',
      description: 'Standard desktop computer for office use',
      type: 'Item',
      uom: 'EA',
      unitPrice: 899.99,
      taxCode: 'STD',
      taxRate: 10
    }, {
      id: 'CAT002-003',
      name: 'Monitor - 24 inch',
      description: '24-inch LED monitor, Full HD',
      type: 'Item',
      uom: 'EA',
      unitPrice: 199.99,
      taxCode: 'STD',
      taxRate: 10
    }, {
      id: 'CAT002-004',
      name: 'IT Support - Basic',
      description: 'Basic IT support package, 10 hours',
      type: 'Service',
      uom: 'HR',
      unitPrice: 75.0,
      taxCode: 'STD',
      taxRate: 10
    }],
    V004: [{
      id: 'CAT004-001',
      name: 'Server - Entry Level',
      description: 'Entry level server for small businesses',
      type: 'Item',
      uom: 'EA',
      unitPrice: 1499.99,
      taxCode: 'STD',
      taxRate: 10
    }, {
      id: 'CAT004-002',
      name: 'Server - Enterprise',
      description: 'Enterprise grade server with high performance',
      type: 'Item',
      uom: 'EA',
      unitPrice: 3999.99,
      taxCode: 'STD',
      taxRate: 10
    }, {
      id: 'CAT004-003',
      name: 'Network Switch - 24 Port',
      description: '24-port managed network switch',
      type: 'Item',
      uom: 'EA',
      unitPrice: 349.99,
      taxCode: 'STD',
      taxRate: 10
    }, {
      id: 'CAT004-004',
      name: 'Server Maintenance',
      description: 'Annual server maintenance package',
      type: 'Service',
      uom: 'YEAR',
      unitPrice: 999.99,
      taxCode: 'STD',
      taxRate: 10
    }]
  });
  // Sample addresses for different contexts
  const [addresses, setAddresses] = useState({
    tenant: [{
      id: 'TA001',
      name: 'Corporate HQ',
      street: '123 Main St',
      city: 'New York',
      state: 'NY',
      postalCode: '10001',
      country: 'United States'
    }, {
      id: 'TA002',
      name: 'Distribution Center',
      street: '456 Warehouse Blvd',
      city: 'Chicago',
      state: 'IL',
      postalCode: '60007',
      country: 'United States'
    }],
    entity: [{
      id: 'EA001',
      name: 'North America Office',
      street: '789 Business Park',
      city: 'Boston',
      state: 'MA',
      postalCode: '02108',
      country: 'United States'
    }, {
      id: 'EA002',
      name: 'European Office',
      street: '10 Tech Square',
      city: 'London',
      postalCode: 'EC1A 1BB',
      country: 'United Kingdom'
    }],
    site: [{
      id: 'SA001',
      name: 'Manufacturing Plant',
      street: '567 Industrial Way',
      city: 'Detroit',
      state: 'MI',
      postalCode: '48127',
      country: 'United States'
    }, {
      id: 'SA002',
      name: 'Research Lab',
      street: '890 Innovation Dr',
      city: 'San Francisco',
      state: 'CA',
      postalCode: '94107',
      country: 'United States'
    }]
  });
  const [showCustomAddress, setShowCustomAddress] = useState(false);
  const [vendorSearchQuery, setVendorSearchQuery] = useState('');
  const [filteredVendors, setFilteredVendors] = useState(vendors);
  const [showCatalogModal, setShowCatalogModal] = useState(false);
  const [currentLineItemIndex, setCurrentLineItemIndex] = useState(0);
  const [catalogSearchQuery, setCatalogSearchQuery] = useState('');
  const [filteredCatalogItems, setFilteredCatalogItems] = useState([]);
  // Generate a unique Purchase Order Number
  const generatePONumber = () => {
    const currentYear = new Date().getFullYear();
    // Generate a random 4-digit number (in a real app, this would be sequential)
    const randomNum = Math.floor(1000 + Math.random() * 9000);
    return `PO-${currentYear}-${randomNum}`;
  };
  // Unit of measure options
  const uomOptions = ['EA', 'KG', 'LB', 'M', 'CM', 'L', 'ML', 'HR', 'DAY', 'MONTH', 'YEAR'];
  // Tax codes with descriptions and rates
  const taxCodes = [{
    code: 'STD',
    name: 'Standard Rate',
    rate: 10
  }, {
    code: 'RED',
    name: 'Reduced Rate',
    rate: 5
  }, {
    code: 'ZERO',
    name: 'Zero Rate',
    rate: 0
  }, {
    code: 'EXM',
    name: 'Exempt',
    rate: 0
  }];
  // Function to check if an item description suggests office supplies
  const isOfficeSupplies = description => {
    const officeSupplyTerms = ['pen', 'pencil', 'paper', 'notebook', 'stapler', 'clip', 'folder', 'binder'];
    return officeSupplyTerms.some(term => description.toLowerCase().includes(term));
  };
  // Function to get GL code suggestion based on item description
  const getGLCodeSuggestion = item => {
    if (isOfficeSupplies(item.description)) {
      return {
        code: '6100.100',
        name: 'Expenses, Office Supplies'
      };
    }
    return null;
  };
  // Function to apply the suggested GL code
  const applySuggestedGLCode = (index, glCode) => {
    handleItemChange(index, 'glCode', glCode);
  };
  useEffect(() => {
    if (initialData) {
      // If we have initial data (for conversion from requisition)
      setFormData({
        name: initialData.name || '',
        vendor: initialData.vendor || '',
        vendorId: initialData.vendorId || '',
        items: initialData.items && initialData.items.length > 0 ? initialData.items.map(item => ({
          ...item,
          type: item.type || 'Item',
          uom: item.uom || 'EA',
          taxCode: item.taxCode || 'STD',
          taxRate: item.taxRate || 10,
          taxAmount: item.taxAmount || 0,
          catalogItemId: item.catalogItemId || ''
        })) : [{
          description: '',
          quantity: 1,
          unitPrice: 0,
          type: 'Item',
          uom: 'EA',
          taxCode: 'STD',
          taxRate: 10,
          taxAmount: 0,
          catalogItemId: ''
        }],
        notes: initialData.notes || '',
        deliveryDate: initialData.deliveryDate || '',
        paymentTerms: initialData.paymentTerms || '30',
        shipmentMethod: initialData.shipmentMethod || '',
        currency: initialData.currency || getDefaultCurrency().code,
        deliveryAddress: initialData.deliveryAddress || {
          type: 'entity',
          addressId: '',
          customAddress: {
            name: '',
            street: '',
            city: '',
            state: '',
            postalCode: '',
            country: ''
          }
        },
        deliveryContact: initialData.deliveryContact || {
          name: '',
          phone: '',
          email: ''
        }
      });
    } else {
      // For new POs, generate a PO number automatically
      setFormData(prevData => ({
        ...prevData,
        name: generatePONumber()
      }));
    }
  }, [initialData]);
  useEffect(() => {
    // Filter vendors based on search query
    if (vendorSearchQuery) {
      const filtered = vendors.filter(vendor => vendor.name.toLowerCase().includes(vendorSearchQuery.toLowerCase()) || vendor.id.toLowerCase().includes(vendorSearchQuery.toLowerCase()));
      setFilteredVendors(filtered);
    } else {
      setFilteredVendors(vendors);
    }
  }, [vendorSearchQuery, vendors]);
  useEffect(() => {
    // Filter catalog items based on search query
    if (showCatalogModal && formData.vendorId) {
      const catalog = vendorCatalogs[formData.vendorId] || [];
      if (catalogSearchQuery) {
        const filtered = catalog.filter(item => item.name.toLowerCase().includes(catalogSearchQuery.toLowerCase()) || item.description.toLowerCase().includes(catalogSearchQuery.toLowerCase()) || item.id.toLowerCase().includes(catalogSearchQuery.toLowerCase()));
        setFilteredCatalogItems(filtered);
      } else {
        setFilteredCatalogItems(catalog);
      }
    }
  }, [catalogSearchQuery, formData.vendorId, showCatalogModal, vendorCatalogs]);
  const handleChange = e => {
    const {
      name,
      value
    } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  const handleItemChange = (index, field, value) => {
    const updatedItems = [...formData.items];
    if (field === 'taxCode') {
      // Find the tax rate for the selected tax code
      const selectedTaxCode = taxCodes.find(tax => tax.code === value);
      const taxRate = selectedTaxCode ? selectedTaxCode.rate : 0;
      updatedItems[index] = {
        ...updatedItems[index],
        [field]: value,
        taxRate: taxRate
      };
      // Recalculate tax amount
      const subtotal = updatedItems[index].quantity * updatedItems[index].unitPrice;
      updatedItems[index].taxAmount = subtotal * taxRate / 100;
    } else {
      updatedItems[index] = {
        ...updatedItems[index],
        [field]: field === 'quantity' || field === 'unitPrice' ? parseFloat(value) || 0 : value
      };
      // Recalculate tax amount if quantity or unit price changes
      if (field === 'quantity' || field === 'unitPrice') {
        const subtotal = updatedItems[index].quantity * updatedItems[index].unitPrice;
        updatedItems[index].taxAmount = subtotal * updatedItems[index].taxRate / 100;
      }
    }
    setFormData({
      ...formData,
      items: updatedItems
    });
  };
  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, {
        description: '',
        quantity: 1,
        unitPrice: 0,
        type: 'Item',
        uom: 'EA',
        taxCode: 'STD',
        taxRate: 10,
        taxAmount: 0,
        catalogItemId: ''
      }]
    });
  };
  const removeItem = index => {
    const updatedItems = [...formData.items];
    updatedItems.splice(index, 1);
    setFormData({
      ...formData,
      items: updatedItems
    });
  };
  const calculateSubtotal = () => {
    return formData.items.reduce((sum, item) => sum + item.quantity * item.unitPrice, 0);
  };
  const calculateTaxTotal = () => {
    return formData.items.reduce((sum, item) => sum + item.taxAmount, 0);
  };
  const calculateTotal = () => {
    return calculateSubtotal() + calculateTaxTotal();
  };
  const handleVendorSelect = vendor => {
    setFormData({
      ...formData,
      vendor: vendor.name,
      vendorId: vendor.id
    });
    setVendorSearchQuery('');
  };
  const handleAddressTypeChange = e => {
    const addressType = e.target.value;
    setFormData({
      ...formData,
      deliveryAddress: {
        ...formData.deliveryAddress,
        type: addressType,
        addressId: ''
      }
    });
    setShowCustomAddress(addressType === 'custom');
  };
  const handleAddressSelect = e => {
    const addressId = e.target.value;
    setFormData({
      ...formData,
      deliveryAddress: {
        ...formData.deliveryAddress,
        addressId: addressId
      }
    });
  };
  const handleCustomAddressChange = e => {
    const {
      name,
      value
    } = e.target;
    setFormData({
      ...formData,
      deliveryAddress: {
        ...formData.deliveryAddress,
        customAddress: {
          ...formData.deliveryAddress.customAddress,
          [name.replace('customAddress_', '')]: value
        }
      }
    });
  };
  const handleDeliveryContactChange = e => {
    const {
      name,
      value
    } = e.target;
    setFormData({
      ...formData,
      deliveryContact: {
        ...formData.deliveryContact,
        [name.replace('deliveryContact_', '')]: value
      }
    });
  };
  const openCatalogModal = index => {
    setCurrentLineItemIndex(index);
    setCatalogSearchQuery('');
    // Reset filtered catalog items to show all items for the vendor
    if (formData.vendorId && vendorCatalogs[formData.vendorId]) {
      setFilteredCatalogItems(vendorCatalogs[formData.vendorId]);
    } else {
      setFilteredCatalogItems([]);
    }
    setShowCatalogModal(true);
  };
  const closeCatalogModal = () => {
    setShowCatalogModal(false);
    setCatalogSearchQuery('');
  };
  const handleCatalogItemSelect = catalogItem => {
    const updatedItems = [...formData.items];
    // Calculate tax amount
    const taxAmount = catalogItem.unitPrice * catalogItem.taxRate / 100;
    // Update the line item with catalog item details
    updatedItems[currentLineItemIndex] = {
      description: catalogItem.description,
      quantity: 1,
      unitPrice: catalogItem.unitPrice,
      type: catalogItem.type,
      uom: catalogItem.uom,
      taxCode: catalogItem.taxCode,
      taxRate: catalogItem.taxRate,
      taxAmount: taxAmount,
      catalogItemId: catalogItem.id,
      itemCode: catalogItem.id // Set the itemCode to the catalog item's ID
    };
    setFormData({
      ...formData,
      items: updatedItems
    });
    closeCatalogModal();
  };
  const handleSubmit = e => {
    e.preventDefault();
    // Validate line items
    const lineItemErrors = formData.items.map(item => {
      const itemErrors = {};
      if (!item.description) {
        itemErrors.description = 'Description is required';
      }
      if (!item.quantity || item.quantity <= 0) {
        itemErrors.quantity = 'Quantity must be greater than 0';
      }
      return Object.keys(itemErrors).length > 0 ? itemErrors : null;
    });
    // Update errors state
    setErrors({
      lineItems: lineItemErrors
    });
    // Only save if there are no errors
    if (!lineItemErrors.some(error => error !== null)) {
      onSave({
        ...formData,
        total: calculateTotal()
      }, initialData);
    }
  };
  // Check if the selected vendor has a catalog
  const selectedVendorHasCatalog = () => {
    if (!formData.vendorId) return false;
    const vendor = vendors.find(v => v.id === formData.vendorId);
    return vendor ? vendor.hasCatalog : false;
  };
  return <form onSubmit={handleSubmit} data-id="element-11662">
      <div className="shadow sm:rounded-md sm:overflow-hidden" data-id="element-11663">
        <div className="px-4 py-5 bg-white space-y-6 sm:p-6" data-id="element-11664">
          <div className="grid grid-cols-6 gap-6" data-id="element-11665">
            <div className="col-span-6 sm:col-span-3" data-id="element-11666">
              <label htmlFor="name" className="block text-sm font-medium text-gray-700" data-id="element-11667">
                Purchase Order Number
              </label>
              <div className="mt-1 flex rounded-md shadow-sm" data-id="element-11668">
                <input type="text" name="name" id="name" value={formData.name} onChange={handleChange} className="focus:ring-indigo-500 focus:border-indigo-500 flex-1 block w-full rounded-md sm:text-sm border-gray-300 bg-gray-50" readOnly={!initialData} data-id="element-11669" />
              </div>
              {!initialData && <p className="mt-1 text-xs text-gray-500" data-id="element-11670">
                  Automatically generated purchase order number
                </p>}
            </div>
            {/* Vendor Lookup */}
            <div className="col-span-6 sm:col-span-3" data-id="element-11671">
              <label htmlFor="vendor" className="block text-sm font-medium text-gray-700" data-id="element-11672">
                Vendor
              </label>
              <div className="mt-1 relative" data-id="element-11673">
                <div className="flex" data-id="element-11674">
                  <div className="relative flex-grow" data-id="element-11675">
                    <input type="text" id="vendorSearch" value={vendorSearchQuery} onChange={e => setVendorSearchQuery(e.target.value)} placeholder="Search vendors..." className="focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" data-id="element-11676" />
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none" data-id="element-11677">
                      <SearchIcon className="h-4 w-4 text-gray-400" data-id="element-11678" />
                    </div>
                  </div>
                </div>
                {/* Selected vendor display */}
                {formData.vendor && <div className="mt-2 flex items-center p-2 bg-gray-50 border border-gray-200 rounded-md" data-id="element-11679">
                    <span className="text-sm font-medium text-gray-700" data-id="element-11680">
                      {formData.vendor}
                    </span>
                    <span className="ml-2 text-xs text-gray-500" data-id="element-11681">
                      ({formData.vendorId})
                    </span>
                    {selectedVendorHasCatalog() && <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-11682">
                        <BookOpenIcon className="h-3 w-3 mr-1" data-id="element-11683" />
                        Catalog Available
                      </span>}
                  </div>}
                {/* Vendor search results */}
                {vendorSearchQuery && <div className="absolute z-10 mt-1 w-full bg-white shadow-lg rounded-md overflow-hidden" data-id="element-11684">
                    <ul className="max-h-60 overflow-auto" data-id="element-11685">
                      {filteredVendors.length > 0 ? filteredVendors.map(vendor => <li key={vendor.id} className="cursor-pointer p-2 hover:bg-gray-50" onClick={() => handleVendorSelect(vendor)} data-id="element-11686">
                            <div className="flex justify-between" data-id="element-11687">
                              <span className="font-medium" data-id="element-11688">{vendor.name}</span>
                              <span className="text-xs text-gray-500" data-id="element-11689">
                                {vendor.id}
                              </span>
                            </div>
                            <div className="flex items-center" data-id="element-11690">
                              <span className="text-xs text-gray-500" data-id="element-11691">
                                {vendor.country}
                              </span>
                              {vendor.hasCatalog && <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-11692">
                                  <BookOpenIcon className="h-3 w-3 mr-1" data-id="element-11693" />
                                  Catalog
                                </span>}
                            </div>
                          </li>) : <li className="p-2 text-center text-sm text-gray-500" data-id="element-11694">
                          No vendors found
                        </li>}
                    </ul>
                  </div>}
              </div>
            </div>
            <div className="col-span-6 sm:col-span-3" data-id="element-11695">
              <label htmlFor="deliveryDate" className="block text-sm font-medium text-gray-700" data-id="element-11696">
                Expected Delivery Date
              </label>
              <input type="date" name="deliveryDate" id="deliveryDate" value={formData.deliveryDate} onChange={handleChange} className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" data-id="element-11697" />
            </div>
            <div className="col-span-6 sm:col-span-3" data-id="element-11698">
              <label htmlFor="paymentTerms" className="block text-sm font-medium text-gray-700" data-id="element-11699">
                Payment Terms
              </label>
              <select id="paymentTerms" name="paymentTerms" value={formData.paymentTerms} onChange={handleChange} className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" data-id="element-11700">
                <option value="15" data-id="element-11701">Net 15</option>
                <option value="30" data-id="element-11702">Net 30</option>
                <option value="45" data-id="element-11703">Net 45</option>
                <option value="60" data-id="element-11704">Net 60</option>
                <option value="immediate" data-id="element-11705">Immediate Payment</option>
              </select>
            </div>
            <div className="col-span-6 sm:col-span-3" data-id="element-11706">
              <label htmlFor="shipmentMethod" className="block text-sm font-medium text-gray-700" data-id="element-11707">
                Shipment Method
              </label>
              <input type="text" name="shipmentMethod" id="shipmentMethod" value={formData.shipmentMethod} onChange={handleChange} className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" data-id="element-11708" />
            </div>
            <div className="col-span-6 sm:col-span-3" data-id="element-11709">
              <label htmlFor="currency" className="block text-sm font-medium text-gray-700" data-id="element-11710">
                Currency
              </label>
              <select id="currency" name="currency" value={formData.currency} onChange={handleChange} className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" data-id="element-11711">
                <option value="AUD" data-id="element-11712">AUD - Australian Dollar</option>
                <option value="USD" data-id="element-11713">USD - US Dollar</option>
                <option value="EUR" data-id="element-11714">EUR - Euro</option>
                <option value="GBP" data-id="element-11715">GBP - British Pound</option>
                <option value="CAD" data-id="element-11716">CAD - Canadian Dollar</option>
                <option value="JPY" data-id="element-11717">JPY - Japanese Yen</option>
              </select>
            </div>
            {/* Delivery Address Section */}
            <div className="col-span-6" data-id="element-11718">
              <fieldset className="border border-gray-200 rounded-md p-4" data-id="element-11719">
                <legend className="text-sm font-medium text-gray-700 px-2" data-id="element-11720">
                  Delivery Address
                </legend>
                <div className="mb-4" data-id="element-11721">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-11722">
                    Address Type
                  </label>
                  <div className="flex space-x-4" data-id="element-11723">
                    <div className="flex items-center" data-id="element-11724">
                      <input id="addressTypeTenant" name="addressType" type="radio" value="tenant" checked={formData.deliveryAddress.type === 'tenant'} onChange={handleAddressTypeChange} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" data-id="element-11725" />
                      <label htmlFor="addressTypeTenant" className="ml-2 block text-sm text-gray-700" data-id="element-11726">
                        Tenant Address
                      </label>
                    </div>
                    <div className="flex items-center" data-id="element-11727">
                      <input id="addressTypeEntity" name="addressType" type="radio" value="entity" checked={formData.deliveryAddress.type === 'entity'} onChange={handleAddressTypeChange} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" data-id="element-11728" />
                      <label htmlFor="addressTypeEntity" className="ml-2 block text-sm text-gray-700" data-id="element-11729">
                        Entity Address
                      </label>
                    </div>
                    <div className="flex items-center" data-id="element-11730">
                      <input id="addressTypeSite" name="addressType" type="radio" value="site" checked={formData.deliveryAddress.type === 'site'} onChange={handleAddressTypeChange} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" data-id="element-11731" />
                      <label htmlFor="addressTypeSite" className="ml-2 block text-sm text-gray-700" data-id="element-11732">
                        Site Address
                      </label>
                    </div>
                    <div className="flex items-center" data-id="element-11733">
                      <input id="addressTypeCustom" name="addressType" type="radio" value="custom" checked={formData.deliveryAddress.type === 'custom'} onChange={handleAddressTypeChange} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" data-id="element-11734" />
                      <label htmlFor="addressTypeCustom" className="ml-2 block text-sm text-gray-700" data-id="element-11735">
                        Custom Address
                      </label>
                    </div>
                  </div>
                </div>
                {formData.deliveryAddress.type !== 'custom' && <div className="mb-4" data-id="element-11736">
                    <label htmlFor="addressSelect" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-11737">
                      Select Address
                    </label>
                    <select id="addressSelect" value={formData.deliveryAddress.addressId} onChange={handleAddressSelect} className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" data-id="element-11738">
                      <option value="" data-id="element-11739">Select an address</option>
                      {addresses[formData.deliveryAddress.type].map(address => <option key={address.id} value={address.id} data-id="element-11740">
                            {address.name} - {address.street}, {address.city}
                          </option>)}
                    </select>
                    {/* Display selected address details */}
                    {formData.deliveryAddress.addressId && <div className="mt-3 p-3 bg-gray-50 rounded-md" data-id="element-11741">
                        {(() => {
                    const selectedAddress = addresses[formData.deliveryAddress.type].find(addr => addr.id === formData.deliveryAddress.addressId);
                    if (selectedAddress) {
                      return <div className="flex items-start" data-id="element-11742">
                                <MapPinIcon className="h-5 w-5 text-gray-400 mt-0.5 mr-2" data-id="element-11743" />
                                <div data-id="element-11744">
                                  <p className="text-sm font-medium" data-id="element-11745">
                                    {selectedAddress.name}
                                  </p>
                                  <p className="text-sm text-gray-500" data-id="element-11746">
                                    {selectedAddress.street},{' '}
                                    {selectedAddress.city},
                                    {selectedAddress.state && ` ${selectedAddress.state},`}{' '}
                                    {selectedAddress.postalCode}
                                  </p>
                                  <p className="text-sm text-gray-500" data-id="element-11747">
                                    {selectedAddress.country}
                                  </p>
                                </div>
                              </div>;
                    }
                    return null;
                  })()}
                      </div>}
                  </div>}
                {formData.deliveryAddress.type === 'custom' && <div className="grid grid-cols-1 gap-y-4 gap-x-4 sm:grid-cols-6" data-id="element-11748">
                    <div className="sm:col-span-6" data-id="element-11749">
                      <label htmlFor="customAddress_name" className="block text-sm font-medium text-gray-700" data-id="element-11750">
                        Address Name/Label
                      </label>
                      <input type="text" name="customAddress_name" id="customAddress_name" value={formData.deliveryAddress.customAddress.name} onChange={handleCustomAddressChange} className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" placeholder="e.g., Warehouse, Client Site" data-id="element-11751" />
                    </div>
                    <div className="sm:col-span-6" data-id="element-11752">
                      <label htmlFor="customAddress_street" className="block text-sm font-medium text-gray-700" data-id="element-11753">
                        Street Address
                      </label>
                      <input type="text" name="customAddress_street" id="customAddress_street" value={formData.deliveryAddress.customAddress.street} onChange={handleCustomAddressChange} className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" data-id="element-11754" />
                    </div>
                    <div className="sm:col-span-3" data-id="element-11755">
                      <label htmlFor="customAddress_city" className="block text-sm font-medium text-gray-700" data-id="element-11756">
                        City
                      </label>
                      <input type="text" name="customAddress_city" id="customAddress_city" value={formData.deliveryAddress.customAddress.city} onChange={handleCustomAddressChange} className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" data-id="element-11757" />
                    </div>
                    <div className="sm:col-span-3" data-id="element-11758">
                      <label htmlFor="customAddress_state" className="block text-sm font-medium text-gray-700" data-id="element-11759">
                        State / Province
                      </label>
                      <input type="text" name="customAddress_state" id="customAddress_state" value={formData.deliveryAddress.customAddress.state} onChange={handleCustomAddressChange} className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" data-id="element-11760" />
                    </div>
                    <div className="sm:col-span-3" data-id="element-11761">
                      <label htmlFor="customAddress_postalCode" className="block text-sm font-medium text-gray-700" data-id="element-11762">
                        Postal Code
                      </label>
                      <input type="text" name="customAddress_postalCode" id="customAddress_postalCode" value={formData.deliveryAddress.customAddress.postalCode} onChange={handleCustomAddressChange} className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" data-id="element-11763" />
                    </div>
                    <div className="sm:col-span-3" data-id="element-11764">
                      <label htmlFor="customAddress_country" className="block text-sm font-medium text-gray-700" data-id="element-11765">
                        Country
                      </label>
                      <input type="text" name="customAddress_country" id="customAddress_country" value={formData.deliveryAddress.customAddress.country} onChange={handleCustomAddressChange} className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" data-id="element-11766" />
                    </div>
                  </div>}
                {/* Add Delivery Contact after the address selection/entry */}
                <div className="mt-6 pt-4 border-t border-gray-200" data-id="element-11767">
                  <h4 className="text-sm font-medium text-gray-700 mb-3" data-id="element-11768">
                    Delivery Contact
                  </h4>
                  <div className="grid grid-cols-1 gap-y-4 gap-x-4 sm:grid-cols-6" data-id="element-11769">
                    <div className="sm:col-span-6" data-id="element-11770">
                      <label htmlFor="deliveryContact_name" className="block text-sm font-medium text-gray-700" data-id="element-11771">
                        Contact Name
                      </label>
                      <input type="text" name="deliveryContact_name" id="deliveryContact_name" value={formData.deliveryContact.name} onChange={handleDeliveryContactChange} className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" placeholder="Name of person to contact for delivery" data-id="element-11772" />
                    </div>
                    <div className="sm:col-span-3" data-id="element-11773">
                      <label htmlFor="deliveryContact_phone" className="block text-sm font-medium text-gray-700" data-id="element-11774">
                        Phone Number
                      </label>
                      <input type="text" name="deliveryContact_phone" id="deliveryContact_phone" value={formData.deliveryContact.phone} onChange={handleDeliveryContactChange} className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" placeholder="Contact phone number" data-id="element-11775" />
                    </div>
                    <div className="sm:col-span-3" data-id="element-11776">
                      <label htmlFor="deliveryContact_email" className="block text-sm font-medium text-gray-700" data-id="element-11777">
                        Email Address
                      </label>
                      <input type="email" name="deliveryContact_email" id="deliveryContact_email" value={formData.deliveryContact.email} onChange={handleDeliveryContactChange} className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" placeholder="Contact email address" data-id="element-11778" />
                    </div>
                  </div>
                </div>
              </fieldset>
            </div>
          </div>
          <div data-id="element-11779">
            <h3 className="text-lg font-medium leading-6 text-gray-900" data-id="element-11780">
              Line Items
            </h3>
            <div className="mt-4 space-y-4" data-id="element-11781">
              {formData.items.map((item, index) => <div key={item.id} className="mb-6 border border-gray-200 rounded-lg overflow-hidden" data-id="element-11782">
                  <div className="px-4 py-3 bg-gray-50 flex justify-between items-center border-b border-gray-200" data-id="element-11783">
                    <h4 className="text-base font-medium text-gray-900" data-id="element-11784">
                      Item {index + 1}
                    </h4>
                    <button type="button" onClick={() => removeItem(index)} className="text-red-600 hover:text-red-900" data-id="element-11785">
                      <TrashIcon className="h-5 w-5" data-id="element-11786" />
                    </button>
                  </div>
                  {item.catalogItemId && <div className="mb-4 p-2 bg-blue-50 border border-blue-200 rounded-md" data-id="element-11787">
                      <div className="flex items-center" data-id="element-11788">
                        <BookOpenIcon className="h-4 w-4 text-blue-500 mr-2" data-id="element-11789" />
                        <span className="text-sm font-medium text-blue-700" data-id="element-11790">
                          Catalog Item: {item.catalogItemId}
                        </span>
                      </div>
                    </div>}
                  <div className="p-4" data-id="element-11791">
                    <div className="grid grid-cols-1 gap-y-4 gap-x-6 sm:grid-cols-6" data-id="element-11792">
                      <div className="sm:col-span-3" data-id="element-11793">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-11794">
                          Type
                        </label>
                        <select className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" value={item.type} onChange={e => handleItemChange(index, 'type', e.target.value)} data-id="element-11795">
                          <option value="Item" data-id="element-11796">Item</option>
                          <option value="Service" data-id="element-11797">Service</option>
                        </select>
                      </div>
                      <div className="sm:col-span-3" data-id="element-11798">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-11799">
                          Item Code
                        </label>
                        <div className="flex" data-id="element-11800">
                          <input type="text" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" value={item.itemCode || ''} onChange={e => handleItemChange(index, 'itemCode', e.target.value)} placeholder="Item code" data-id="element-11801" />
                          <button type="button" onClick={() => openCatalogModal(index)} className="ml-2 inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-11802">
                            Select from Catalog
                          </button>
                        </div>
                      </div>
                      <div className="sm:col-span-6" data-id="element-11803">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-11804">
                          Description <span className="text-red-500" data-id="element-11805">*</span>
                        </label>
                        <input type="text" className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.lineItems && errors.lineItems[index]?.description ? 'border-red-300' : ''}`} value={item.description} onChange={e => handleItemChange(index, 'description', e.target.value)} placeholder="Brief description" data-id="element-11806" />
                        {errors.lineItems && errors.lineItems[index]?.description && <p className="mt-1 text-sm text-red-600" data-id="element-11807">
                              {errors.lineItems[index].description}
                            </p>}
                      </div>
                      <div className="sm:col-span-2" data-id="element-11808">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-11809">
                          Quantity <span className="text-red-500" data-id="element-11810">*</span>
                        </label>
                        <input type="number" className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.lineItems && errors.lineItems[index]?.quantity ? 'border-red-300' : ''}`} value={item.quantity} onChange={e => handleItemChange(index, 'quantity', parseFloat(e.target.value) || 0)} min="0" step="1" data-id="element-11811" />
                        {errors.lineItems && errors.lineItems[index]?.quantity && <p className="mt-1 text-sm text-red-600" data-id="element-11812">
                              {errors.lineItems[index].quantity}
                            </p>}
                      </div>
                      <div className="sm:col-span-2" data-id="element-11813">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-11814">
                          Unit of Measure
                        </label>
                        <select className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" value={item.uom} onChange={e => handleItemChange(index, 'uom', e.target.value)} data-id="element-11815">
                          {uomOptions.map(uom => <option key={uom} value={uom} data-id="element-11816">
                              {uom}
                            </option>)}
                        </select>
                      </div>
                      <div className="sm:col-span-2" data-id="element-11817">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-11818">
                          Unit Price <span className="text-red-500" data-id="element-11819">*</span>
                        </label>
                        <input type="number" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" value={item.unitPrice} onChange={e => handleItemChange(index, 'unitPrice', parseFloat(e.target.value) || 0)} min="0" step="0.01" data-id="element-11820" />
                      </div>
                      <div className="sm:col-span-2" data-id="element-11821">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-11822">
                          Tax Code
                        </label>
                        <select className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" value={item.taxCode} onChange={e => handleItemChange(index, 'taxCode', e.target.value)} data-id="element-11823">
                          {taxCodes.map(code => <option key={code.code} value={code.code} data-id="element-11824">
                              {code.code} - {code.name}
                            </option>)}
                        </select>
                      </div>
                      <div className="sm:col-span-2" data-id="element-11825">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-11826">
                          Tax Amount
                        </label>
                        <div className="p-2 bg-gray-50 rounded-md border border-gray-300 text-sm font-medium" data-id="element-11827">
                          {formatCurrency(item.taxAmount || 0, formData.currency || 'USD', locale)}
                        </div>
                      </div>
                      <div className="sm:col-span-2" data-id="element-11828">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-11829">
                          GL Code
                        </label>
                        <input type="text" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" value={item.glCode || ''} onChange={e => handleItemChange(index, 'glCode', e.target.value)} placeholder="GL account code" data-id="element-11830" />
                        {/* Remi suggestion for GL Code */}
                        {getGLCodeSuggestion(item) && !item.glCode && <div className="mt-1 flex items-center p-2 bg-blue-50 border-l-4 border-blue-400 rounded text-sm" data-id="element-11831">
                            <InfoIcon className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" data-id="element-11832" />
                            <div className="flex-grow" data-id="element-11833">
                              <p className="text-blue-700" data-id="element-11834">
                                Remi suggests:{' '}
                                <span className="font-mono font-medium" data-id="element-11835">
                                  {getGLCodeSuggestion(item).code}
                                </span>
                                <span className="ml-1" data-id="element-11836">
                                  ({getGLCodeSuggestion(item).name})
                                </span>
                              </p>
                            </div>
                            <button type="button" onClick={() => applySuggestedGLCode(index, getGLCodeSuggestion(item).code)} className="ml-2 px-2 py-1 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded text-xs font-medium" data-id="element-11837">
                              Apply
                            </button>
                          </div>}
                      </div>
                      <div className="sm:col-span-2" data-id="element-11838">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-11839">
                          Line Total
                        </label>
                        <div className="p-2 bg-gray-50 rounded-md border border-gray-300 text-lg font-medium" data-id="element-11840">
                          {formatCurrency(item.quantity * item.unitPrice, formData.currency || 'USD', locale)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>)}
              <div className="mt-2" data-id="element-11841">
                <button type="button" onClick={addItem} className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-11842">
                  <PlusIcon className="h-4 w-4 mr-1" data-id="element-11843" />
                  Add Line Item
                </button>
              </div>
            </div>
          </div>
          <div data-id="element-11844">
            <label htmlFor="notes" className="block text-sm font-medium text-gray-700" data-id="element-11845">
              Notes
            </label>
            <div className="mt-1" data-id="element-11846">
              <textarea id="notes" name="notes" rows={3} value={formData.notes} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 mt-1 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-11847"></textarea>
            </div>
          </div>
          <div className="border-t border-gray-200 pt-4" data-id="element-11848">
            <div className="flex justify-end" data-id="element-11849">
              <div className="w-64 bg-gray-50 p-4 rounded-md border border-gray-200" data-id="element-11850">
                <div className="flex justify-between items-center text-sm" data-id="element-11851">
                  <span className="text-gray-700" data-id="element-11852">Subtotal:</span>
                  <span className="font-medium" data-id="element-11853">
                    {formatCurrency(calculateSubtotal(), formData.currency, locale)}
                  </span>
                </div>
                <div className="flex justify-between items-center text-sm mt-2" data-id="element-11854">
                  <span className="text-gray-700" data-id="element-11855">Tax:</span>
                  <span className="font-medium" data-id="element-11856">
                    {formatCurrency(calculateTaxTotal(), formData.currency, locale)}
                  </span>
                </div>
                <div className="border-t border-gray-200 mt-2 pt-2 flex justify-between items-center" data-id="element-11857">
                  <span className="text-gray-900 font-medium" data-id="element-11858">Total:</span>
                  <span className="text-lg font-bold" data-id="element-11859">
                    {formatCurrency(calculateTotal(), formData.currency, locale)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="px-4 py-3 bg-gray-50 text-right sm:px-6" data-id="element-11860">
          <button type="submit" className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-11861">
            {isConversion ? 'Create Purchase Order' : 'Save Purchase Order'}
          </button>
        </div>
      </div>
      {/* Catalog Item Selection Modal */}
      {showCatalogModal && <div className="fixed z-50 inset-0 overflow-y-auto" data-id="element-11862">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-11863">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true" onClick={closeCatalogModal} data-id="element-11864">
              <div className="absolute inset-0 bg-gray-500 opacity-75" data-id="element-11865"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" data-id="element-11866">
              &#8203;
            </span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full" data-id="element-11867">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4" data-id="element-11868">
                <div className="sm:flex sm:items-start" data-id="element-11869">
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full" data-id="element-11870">
                    <div className="flex justify-between items-center mb-4" data-id="element-11871">
                      <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-11872">
                        Select Item from Catalog
                      </h3>
                      <button type="button" onClick={closeCatalogModal} className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none" data-id="element-11873">
                        <XIcon className="h-6 w-6" data-id="element-11874" />
                      </button>
                    </div>
                    <div className="mb-4" data-id="element-11875">
                      <div className="relative rounded-md shadow-sm" data-id="element-11876">
                        <input type="text" value={catalogSearchQuery} onChange={e => setCatalogSearchQuery(e.target.value)} className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pr-10 sm:text-sm border-gray-300 rounded-md" placeholder="Search catalog items..." data-id="element-11877" />
                        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none" data-id="element-11878">
                          <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-11879" />
                        </div>
                      </div>
                    </div>
                    <div className="mt-2 max-h-96 overflow-y-auto" data-id="element-11880">
                      <table className="min-w-full divide-y divide-gray-200" data-id="element-11881">
                        <thead className="bg-gray-50" data-id="element-11882">
                          <tr data-id="element-11883">
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11884">
                              Item Code
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11885">
                              Name
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11886">
                              Type
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11887">
                              UoM
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11888">
                              Price
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11889">
                              Tax
                            </th>
                            <th scope="col" className="relative px-6 py-3" data-id="element-11890">
                              <span className="sr-only" data-id="element-11891">Select</span>
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200" data-id="element-11892">
                          {filteredCatalogItems.length > 0 ? filteredCatalogItems.map(catalogItem => <tr key={catalogItem.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => handleCatalogItemSelect(catalogItem)} data-id="element-11893">
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-11894">
                                  {catalogItem.id}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-11895">
                                  <div data-id="element-11896">
                                    <div className="font-medium text-gray-900" data-id="element-11897">
                                      {catalogItem.name}
                                    </div>
                                    <div className="text-xs text-gray-500" data-id="element-11898">
                                      {catalogItem.description}
                                    </div>
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-11899">
                                  {catalogItem.type}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-11900">
                                  {catalogItem.uom}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-11901">
                                  {formatCurrency(catalogItem.unitPrice, formData.currency, locale)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-11902">
                                  {catalogItem.taxCode} ({catalogItem.taxRate}%)
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-11903">
                                  <button type="button" onClick={e => {
                            e.stopPropagation();
                            handleCatalogItemSelect(catalogItem);
                          }} className="text-indigo-600 hover:text-indigo-900" data-id="element-11904">
                                    Select
                                  </button>
                                </td>
                              </tr>) : <tr data-id="element-11905">
                              <td colSpan={7} className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center" data-id="element-11906">
                                {formData.vendorId && vendorCatalogs[formData.vendorId] ? 'No matching catalog items found' : 'No catalog items available for this vendor'}
                              </td>
                            </tr>}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse" data-id="element-11907">
                <button type="button" onClick={closeCatalogModal} className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm" data-id="element-11908">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>}
    </form>;
}